import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function ProductRoadmap() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roadmapFeatures, setRoadmapFeatures] = useState([]);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Load roadmap
    try {
      const response = await fetch('/api/competitive-analysis/get-roadmap', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setRoadmapFeatures(data.roadmap || []);
      } else {
        setError('Failed to load product roadmap');
      }
    } catch (error) {
      setError('Error loading roadmap: ' + error.message);
    }

    setLoading(false);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  // Group by quarter
  const byQuarter = {};
  roadmapFeatures.forEach(feature => {
    const quarter = feature.target_quarter || 'Unscheduled';
    if (!byQuarter[quarter]) {
      byQuarter[quarter] = [];
    }
    byQuarter[quarter].push(feature);
  });

  return (
    <Container className="py-5">
      <BackToDashboard module="business-model" />
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <Card className="shadow">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Product Roadmap</h2>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/dashboard/business-model')}
                >
                  ← Back to Dashboard
                </Button>
              </div>

              {error && <Alert variant="danger">{error}</Alert>}

              {roadmapFeatures.length === 0 ? (
                <Alert variant="info">
                  No roadmap items yet. Complete the full Competitive Intelligence workflow to create your product roadmap.
                </Alert>
              ) : (
                <>
                  <Alert variant="success" className="mb-4">
                    Your product development roadmap with {roadmapFeatures.length} planned feature{roadmapFeatures.length !== 1 ? 's' : ''}.
                  </Alert>

                  {Object.keys(byQuarter).sort().map((quarter) => (
                    <div key={quarter} className="mb-4">
                      <h5 className="mb-3">{quarter}</h5>
                      {byQuarter[quarter].map((feature, index) => (
                        <Card key={index} className="mb-2 border-start border-4" style={{
                          borderLeftColor: feature.priority === 'high' ? '#dc3545' :
                                         feature.priority === 'medium' ? '#ffc107' : '#6c757d'
                        }}>
                          <Card.Body className="py-2">
                            <Row className="align-items-center">
                              <Col md={6}>
                                <strong>{feature.feature_name}</strong>
                                <span className={`badge ms-2 ${feature.status === 'roadmap' ? 'bg-primary' :
                                  feature.status === 'considering' ? 'bg-warning' : 'bg-secondary'}`}>
                                  {feature.status || 'roadmap'}
                                </span>
                              </Col>
                              <Col md={2} className="small text-muted">
                                {feature.category || 'Uncategorized'}
                              </Col>
                              <Col md={2} className="small text-muted">
                                Priority: {feature.priority || 'Not set'}
                              </Col>
                              <Col md={2} className="small text-muted">
                                {feature.feature_type || 'feature'}
                              </Col>
                            </Row>
                            {feature.notes && (
                              <Row>
                                <Col className="small text-muted mt-2">
                                  <strong>Notes:</strong> {feature.notes}
                                </Col>
                              </Row>
                            )}
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  ))}

                  <Alert variant="secondary" className="mt-4">
                    <strong>💡 How to Use This:</strong><br />
                    - Share this roadmap with your team to align on priorities<br />
                    - Use it in investor/stakeholder presentations<br />
                    - Reference it when making product decisions<br />
                    - Update quarterly as you complete features and identify new ones
                  </Alert>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default ProductRoadmap;
